/**
 * React Query hooks for competitor comparison data.
 *
 * `useCompanyCompetitors` fetches the auto-derived peer list (cheap, 1h stale).
 * `useCompetitorFinancials` fans out one full-financials call per peer in
 * parallel — only enabled when the user has flipped on the "Compare with
 * competitors" toggle.
 *
 * Both write their results into `useFinancialStore` so unrelated components
 * (charts, AI insight cards) can subscribe without re-querying.
 */

import { useEffect } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCompanyCompetitors,
  getCompanyFinancials,
  getSectorPeers,
  addCompanyCompetitor,
  removeCompanyCompetitor,
  type CompanyCompetitorsResponse,
  type SectorPeersResponse,
} from '../../../lib/api/financialApi';
import type { UnifiedFinancialResponse, UnifiedFinancialRecord } from '../../../types/financial';
import { useFinancialStore } from '../../../stores/financialStore';

const STALE_TIME_1_HOUR = 1000 * 60 * 60;
const STALE_TIME_30_MIN = 1000 * 60 * 30;

/**
 * Fetch the persisted/auto-discovered competitor list for a company.
 * Writes the resulting names into the store so other components can react.
 */
export const useCompanyCompetitors = (
  companyName: string | null | undefined,
  topN: number = 20
) => {
  const setCompetitorsList = useFinancialStore((s) => s.setCompetitorsList);
  const setCompetitorsError = useFinancialStore((s) => s.setCompetitorsError);

  const query = useQuery<CompanyCompetitorsResponse>({
    queryKey: ['financial', 'competitors', companyName, topN],
    queryFn: () => getCompanyCompetitors(companyName!, topN),
    enabled: !!companyName,
    staleTime: STALE_TIME_1_HOUR,
    retry: (count, error: any) => {
      const status = error?.response?.status ?? error?.status;
      return status !== 404 && count < 1;
    },
  });

  useEffect(() => {
    if (query.data) {
      setCompetitorsList(query.data.competitors.map((c) => c.name));
      setCompetitorsError(null);
    } else if (query.error) {
      const message =
        (query.error as any)?.response?.data?.detail ??
        (query.error as Error).message ??
        'Failed to load competitors';
      setCompetitorsError(message);
    }
  }, [query.data, query.error, setCompetitorsList, setCompetitorsError]);

  return query;
};

/**
 * Fan-out parallel fetches: one `getCompanyFinancials(name, years)` per peer.
 * Writes each successful response into the store keyed by name.
 *
 * `enabled` should be `compareWithCompetitors && competitorsList.length > 0`.
 */
export const useCompetitorFinancials = (
  competitorNames: string[],
  enabled: boolean,
  years: number = 10
) => {
  const upsertCompetitorData = useFinancialStore((s) => s.upsertCompetitorData);
  const setCompetitorsLoading = useFinancialStore((s) => s.setCompetitorsLoading);
  const setCompetitorProgress = useFinancialStore((s) => s.setCompetitorProgress);

  const results = useQueries({
    queries: competitorNames.map((name) => ({
      queryKey: ['financial', 'company', name, years, undefined],
      queryFn: () => getCompanyFinancials(name, years),
      enabled: enabled && !!name,
      staleTime: STALE_TIME_30_MIN,
      retry: (count: number, error: any) => {
        const status = error?.response?.status ?? error?.status;
        return status !== 404 && count < 1;
      },
      select: (data: UnifiedFinancialResponse) => {
        const sorted = [...(data.financial_data || [])].sort(
          (a: UnifiedFinancialRecord, b: UnifiedFinancialRecord) => a.year - b.year
        );
        return { ...data, financial_data: sorted };
      },
    })),
  });

  const isLoading = enabled && results.some((r) => r.isLoading);

  useEffect(() => {
    setCompetitorsLoading(isLoading);
  }, [isLoading, setCompetitorsLoading]);

  useEffect(() => {
    results.forEach((r, idx) => {
      if (r.data) {
        upsertCompetitorData(competitorNames[idx], r.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results.map((r) => r.dataUpdatedAt).join('|')]);

  const failed = results
    .map((r, idx) => (r.isError ? competitorNames[idx] : null))
    .filter((n): n is string => !!n);

  const successCount = results.filter((r) => r.isSuccess).length;
  const totalCount = competitorNames.length;

  // Mirror real fan-out progress into the store so FinancialDashboardTab's peer
  // SmartLoader can show {loaded}/{total} and per-peer done/failed status.
  // Keyed on the joined failed string — the array identity changes every render.
  const failedKey = failed.join('|');
  useEffect(() => {
    setCompetitorProgress({ loaded: successCount, total: totalCount, failed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCount, totalCount, failedKey, setCompetitorProgress]);

  return {
    isLoading,
    failed,
    successCount,
    totalCount,
  };
};

/**
 * Fetch all companies in the focal company's sector. Used by the
 * Manage Competitors modal to populate the multi-select list.
 */
export const useSectorPeers = (
  companyName: string | null | undefined,
  enabled: boolean = true,
  limit: number = 50,
) =>
  useQuery<SectorPeersResponse>({
    queryKey: ['financial', 'sector-peers', companyName, limit],
    queryFn: () => getSectorPeers(companyName!, limit),
    enabled: !!companyName && enabled,
    staleTime: 1000 * 60 * 30,
  });

/**
 * Add a competitor for a company. On success, updates the cached competitor
 * list and the store so dependent charts react immediately.
 */
export const useAddCompetitor = (companyName: string | null | undefined) => {
  const queryClient = useQueryClient();
  const setCompetitorsList = useFinancialStore((s) => s.setCompetitorsList);

  return useMutation({
    mutationFn: (competitorName: string) =>
      addCompanyCompetitor(companyName!, competitorName),
    onSuccess: (data) => {
      setCompetitorsList(data.competitors.map((c) => c.name));
      queryClient.invalidateQueries({
        queryKey: ['financial', 'competitors', companyName],
      });
    },
  });
};

/**
 * Remove a competitor. Also drops that peer's financial data from the store.
 */
export const useRemoveCompetitor = (companyName: string | null | undefined) => {
  const queryClient = useQueryClient();
  const setCompetitorsList = useFinancialStore((s) => s.setCompetitorsList);

  return useMutation({
    mutationFn: (competitorName: string) =>
      removeCompanyCompetitor(companyName!, competitorName),
    onSuccess: (data) => {
      setCompetitorsList(data.competitors.map((c) => c.name));
      queryClient.invalidateQueries({
        queryKey: ['financial', 'competitors', companyName],
      });
    },
  });
};
